/**
 * アプリケーション全体の色定義
 * Chart.js、SCSS、CSS変数で使用する色を一元管理する
 *
 * バックエンドの色定義 (backend/adonis/app/Constants/ChartColors.js) と対応している
 */

// RGB値からrgb()形式の文字列を生成
export const rgbString = (colorObj) => `rgb(${colorObj.r}, ${colorObj.g}, ${colorObj.b})`;

// RGB値からrgba()形式の文字列を生成
export const rgbaString = (colorObj, alpha = 1) => `rgba(${colorObj.r}, ${colorObj.g}, ${colorObj.b}, ${alpha})`;

// HEX値からRGB値に変換
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  } : null;
};

// RGB値からHEX値に変換
export const rgbToHex = (r, g, b) => `#${[r, g, b].map((x) => {
  const hex = x.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
}).join('')}`;

// 基本色定義（RGB値）
export const CHART_BASE_COLORS = {
  // アプリケーション全体の基本カラー（_color.scss由来）
  BLACK: {
    r: 51,
    g: 51,
    b: 51,
    hex: '#333333',
  },
  ORANGE_MAIN: {
    r: 231,
    g: 162,
    b: 35,
    hex: '#e7a223',
  },
  ORANGE_SUB: {
    r: 240,
    g: 194,
    b: 111,
    hex: '#f0c26f',
  },
  ORANGE_SUB_LIGHT: {
    r: 248,
    g: 230,
    b: 193,
    hex: '#f8e6c1',
  },
  BLUE_MAIN: {
    r: 96,
    g: 178,
    b: 193,
    hex: '#60b2c1',
  },
  BLUE_SUB: {
    r: 222,
    g: 246,
    b: 250,
    hex: '#def6fa',
  },
  BLUE_SUB_LIGHT: {
    r: 240,
    g: 249,
    b: 251,
    hex: '#f0f9fb',
  },
  // 学校/企業管理者ヘッダー帯用（スティールブルー / 白文字コントラスト確保・#3185）
  STEEL_BLUE: {
    r: 70,
    g: 130,
    b: 180,
    hex: '#4682b4',
  },
  GREEN_MAIN: {
    r: 96,
    g: 193,
    b: 146,
    hex: '#60c192',
  },
  GREEN_SUB: {
    r: 173,
    g: 225,
    b: 200,
    hex: '#ade1c8',
  },
  GREEN_SUB_LIGHT: {
    r: 224,
    g: 246,
    b: 241,
    hex: '#e0f6f1',
  },
  GRAY_MAIN: {
    r: 205,
    g: 205,
    b: 205,
    hex: '#cdcdcd',
  },
  GRAY_SUB: {
    r: 242,
    g: 239,
    b: 238,
    hex: '#f2efee',
  },
  GRAY_SIDE: {
    r: 128,
    g: 128,
    b: 128,
    hex: '#808080',
  },
  PINK: {
    r: 247,
    g: 28,
    b: 85,
    hex: '#f71c55',
  },
  // 性別
  GENDER_MALE: {
    r: 217,
    g: 238,
    b: 255,
    hex: '#d9eeff',
  },
  GENDER_FEMALE: {
    r: 255,
    g: 217,
    b: 242,
    hex: '#ffd9f2',
  },
  // その他 - 淡いイエローオレンジ (男女と同等の明るさ 2026-05-25 定例決定)
  GENDER_OTHER: {
    r: 255,
    g: 242,
    b: 205,
    hex: '#fff2cd',
  },
  // 健康相談タイプ（通常の色味に対して少し淡い色味）
  COUNSELING_TYPE_TEXT: {
    r: 176,
    g: 217,
    b: 224,
  },
  COUNSELING_TYPE_ONLINE: {
    r: 243,
    g: 209,
    b: 145,
  },
  COUNSELING_TYPE_VIRTUAL: {
    r: 176,
    g: 224,
    b: 201,
  },
  // ココモニボイス関連（ピンク系）
  COCOMONI_VOICE_PINK: {
    r: 245,
    g: 151,
    b: 187,
  },
  // ココモニボイス（ココロライン下回り時の青）※現在は廃止
  COCOMONI_VOICE_BLUE: {
    r: 133,
    g: 194,
    b: 249,
  },
  // ココモニチェック（ココロの健康診断と同じ青系）
  COCOMONI_CHECK_BLUE: {
    r: 100,
    g: 149,
    b: 237,
  },
  COCOMONI_CHECK_BORDER_BLUE: {
    r: 30,
    g: 144,
    b: 255,
  },
  // WHO5アンケート - オンライン健康相談（オレンジ系）
  WHO5_ONLINE_ORANGE: {
    r: 231,
    g: 162,
    b: 35,
  },
  // WHO5アンケート - テキスト健康相談（青系）
  WHO5_TEXT_BLUE: {
    r: 96,
    g: 178,
    b: 193,
  },
  // WHO5アンケート - バーチャル健康相談（緑系）
  WHO5_VIRTUAL_GREEN: {
    r: 96,
    g: 193,
    b: 146,
  },
  // WHO5アンケート - ココロの健康診断（青系）
  WHO5_HEALTH_CHECK_BLUE: {
    r: 100,
    g: 149,
    b: 237,
  },
  // 汎用青色（アナリティクス等で使用）
  GENERAL_BLUE: {
    r: 26,
    g: 115,
    b: 232,
  },
  // 汎用オレンジ色（アナリティクス等で使用）
  GENERAL_ORANGE: {
    r: 239,
    g: 189,
    b: 64,
  },
  // 横棒グラフ用オレンジ
  BAR_ORANGE: {
    r: 231,
    g: 162,
    b: 35,
    hex: '#e7a223',
  },
  // MaxBarGraph用のグレー（データなし時）
  MAX_BAR_GRAY: {
    r: 238,
    g: 238,
    b: 238,
    hex: '#EEEEEE',
  },
  // グラデーションバー用
  GRADIENT_BOTTOM_BLUE: {
    r: 133,
    g: 194,
    b: 249,
  },
  GRADIENT_MIDDLE_YELLOW: {
    r: 249,
    g: 255,
    b: 159,
  },
  GRADIENT_TOP_RED: {
    r: 255,
    g: 149,
    b: 164,
  },
  // 相談変遷グラフ用
  TRANSITION_ONLINE_BG: {
    r: 242,
    g: 202,
    b: 102,
    a: 0.6,
    rgba: 'rgba(242, 202, 102, .6)',
  },
  TRANSITION_TEXT_BG: {
    r: 186,
    g: 213,
    b: 248,
    a: 0.6,
    rgba: 'rgba(186, 213, 248, .6)',
  },
  // 複数データ系列用のカラーパレット（CocomoniDashboard等）
  PALETTE_RED: {
    r: 255,
    g: 99,
    b: 132,
  },
  PALETTE_BLUE: {
    r: 54,
    g: 162,
    b: 235,
  },
  PALETTE_YELLOW: {
    r: 255,
    g: 206,
    b: 86,
  },
  PALETTE_GREEN: {
    r: 75,
    g: 192,
    b: 192,
  },
  PALETTE_PURPLE: {
    r: 153,
    g: 102,
    b: 255,
  },
  PALETTE_ORANGE: {
    r: 255,
    g: 159,
    b: 64,
  },
  PALETTE_BROWN: {
    r: 205,
    g: 133,
    b: 63,
  },
};

// RGB色を白方向に淡くする（ratio: 0=元の色, 1=白）
const _lightenColor = (color, ratio = 0.6) => ({
  r: Math.round(color.r + (255 - color.r) * ratio),
  g: Math.round(color.g + (255 - color.g) * ratio),
  b: Math.round(color.b + (255 - color.b) * ratio),
});

// 用途別の色定義
export const CHART_COLORS = {
  // 性別
  gender: {
    male: rgbString(CHART_BASE_COLORS.GENDER_MALE),
    female: rgbString(CHART_BASE_COLORS.GENDER_FEMALE),
    other: rgbString(CHART_BASE_COLORS.GENDER_OTHER),
  },
  // 健康相談タイプ
  counselingType: {
    text: rgbString(CHART_BASE_COLORS.COUNSELING_TYPE_TEXT),
    online: rgbString(CHART_BASE_COLORS.COUNSELING_TYPE_ONLINE),
    onlineVirtual: rgbString(CHART_BASE_COLORS.ORANGE_SUB),
    virtual: rgbString(CHART_BASE_COLORS.COUNSELING_TYPE_VIRTUAL),
  },
  // ココモニボイス
  cocomoniVoice: {
    default: rgbString(CHART_BASE_COLORS.COCOMONI_VOICE_PINK),
    defaultRgba: (alpha = 1) => rgbaString(CHART_BASE_COLORS.COCOMONI_VOICE_PINK, alpha),
    belowLine: rgbString(CHART_BASE_COLORS.COCOMONI_VOICE_BLUE), // ココロライン下回り時（廃止）
  },
  // ココモニチェック（ココロの健康診断と同じ青系）
  cocomoniCheck: {
    default: rgbString(CHART_BASE_COLORS.COCOMONI_CHECK_BLUE),
    defaultRgba: (alpha = 1) => rgbaString(CHART_BASE_COLORS.COCOMONI_CHECK_BLUE, alpha),
    borderColor: rgbString(CHART_BASE_COLORS.COCOMONI_CHECK_BORDER_BLUE),
    borderColorRgba: (alpha = 1) => rgbaString(CHART_BASE_COLORS.COCOMONI_CHECK_BORDER_BLUE, alpha),
  },
  // WHO5アンケートタイプ別
  who5Answer: {
    type1: { // オンライン健康相談
      color: CHART_BASE_COLORS.WHO5_ONLINE_ORANGE,
      rgb: rgbString(CHART_BASE_COLORS.WHO5_ONLINE_ORANGE),
      rgba: (alpha = 1) => rgbaString(CHART_BASE_COLORS.WHO5_ONLINE_ORANGE, alpha),
    },
    type2: { // テキスト健康相談
      color: CHART_BASE_COLORS.WHO5_TEXT_BLUE,
      rgb: rgbString(CHART_BASE_COLORS.WHO5_TEXT_BLUE),
      rgba: (alpha = 1) => rgbaString(CHART_BASE_COLORS.WHO5_TEXT_BLUE, alpha),
    },
    type3: { // バーチャル健康相談
      color: CHART_BASE_COLORS.WHO5_VIRTUAL_GREEN,
      rgb: rgbString(CHART_BASE_COLORS.WHO5_VIRTUAL_GREEN),
      rgba: (alpha = 1) => rgbaString(CHART_BASE_COLORS.WHO5_VIRTUAL_GREEN, alpha),
    },
    type4: { // ココロの健康診断
      color: CHART_BASE_COLORS.WHO5_HEALTH_CHECK_BLUE,
      rgb: rgbString(CHART_BASE_COLORS.WHO5_HEALTH_CHECK_BLUE),
      rgba: (alpha = 1) => rgbaString(CHART_BASE_COLORS.WHO5_HEALTH_CHECK_BLUE, alpha),
    },
  },
  // アナリティクスグラフ用
  analytics: {
    // 継続グラフ（回数別）
    count: {
      common: { // オンライン+テキスト
        borderColor: rgbaString(CHART_BASE_COLORS.GENERAL_BLUE, 1),
      },
      text: { // テキスト
        borderColor: rgbaString(CHART_BASE_COLORS.GENERAL_BLUE, 0.3),
      },
      online: { // オンライン
        borderColor: rgbaString(CHART_BASE_COLORS.GENERAL_ORANGE, 0.8),
      },
    },
    // 間隔グラフ用（動的alpha値適用）
    interval: {
      base: CHART_BASE_COLORS.GENERAL_BLUE,
      // alpha値は動的に計算: rgba(26, 115, 232, ${data / 25})
    },
  },
  // 横棒グラフ用
  horizontalBar: {
    default: CHART_BASE_COLORS.BAR_ORANGE.hex,
    // データラベル用の色（値によって分岐）
    dataLabels: {
      textColorLarge: '#ffffff', // 値が大きい場合（棒の内側）
      textColorSmall: '#333333', // 値が小さい場合（棒の外側）
      threshold: 10, // 内側/外側を切り替える閾値
    },
    // モード別グラデーション（相談内容BarGraph用）: counselingTypeベース色から淡い色へ
    modeGradient: {
      text: {
        start: CHART_BASE_COLORS.COUNSELING_TYPE_TEXT,
        end: _lightenColor(CHART_BASE_COLORS.COUNSELING_TYPE_TEXT),
      },
      online: {
        start: CHART_BASE_COLORS.ORANGE_SUB,
        end: _lightenColor(CHART_BASE_COLORS.ORANGE_SUB),
      },
      onlineOnly: {
        start: CHART_BASE_COLORS.COUNSELING_TYPE_ONLINE,
        end: _lightenColor(CHART_BASE_COLORS.COUNSELING_TYPE_ONLINE),
      },
      virtualOnly: {
        start: CHART_BASE_COLORS.COUNSELING_TYPE_VIRTUAL,
        end: _lightenColor(CHART_BASE_COLORS.COUNSELING_TYPE_VIRTUAL),
      },
    },
  },
  // MaxBarGraph用
  maxBar: {
    noData: CHART_BASE_COLORS.MAX_BAR_GRAY.hex,
    // データあり時の色は動的に計算: rgba(26, 115, 232, ${data / 25})
    withData: (value, max = 25) => rgbaString(CHART_BASE_COLORS.GENERAL_BLUE, value / max),
  },
  // グラデーションバー用
  gradient: {
    cssGradient: `linear-gradient(to top, rgb(${CHART_BASE_COLORS.GRADIENT_BOTTOM_BLUE.r}, ${CHART_BASE_COLORS.GRADIENT_BOTTOM_BLUE.g}, ${CHART_BASE_COLORS.GRADIENT_BOTTOM_BLUE.b}), rgb(${CHART_BASE_COLORS.GRADIENT_MIDDLE_YELLOW.r}, ${CHART_BASE_COLORS.GRADIENT_MIDDLE_YELLOW.g}, ${CHART_BASE_COLORS.GRADIENT_MIDDLE_YELLOW.b}), rgb(${CHART_BASE_COLORS.GRADIENT_TOP_RED.r}, ${CHART_BASE_COLORS.GRADIENT_TOP_RED.g}, ${CHART_BASE_COLORS.GRADIENT_TOP_RED.b}))`,
  },
  // 相談変遷グラフ用
  transition: {
    online: CHART_BASE_COLORS.TRANSITION_ONLINE_BG.rgba,
    text: CHART_BASE_COLORS.TRANSITION_TEXT_BG.rgba,
  },
  // アカウントタイプ別（CocomoniDashboard用）
  accountType: {
    student: { // 生徒・プログラム対象者
      backgroundColor: 'rgba(255, 99, 132, 0.5)',
      borderColor: 'rgba(255, 99, 132, 1.0)',
    },
    parent: { // 保護者（現在非表示）
      backgroundColor: 'rgba(96, 178, 193, 0.5)',
      borderColor: 'rgb(96, 178, 193)',
    },
    staff: { // 教職員・企業担当者
      backgroundColor: 'rgba(255, 215, 0, 0.5)',
      borderColor: 'rgba(255, 215, 0, 1.0)',
    },
    monitoring: { // モニタリング
      borderColor: 'rgba(30, 144, 255, 1.0)',
    },
  },
  // カラーパレット（複数データ系列用）
  palette: [
    CHART_BASE_COLORS.PALETTE_RED,
    CHART_BASE_COLORS.PALETTE_BLUE,
    CHART_BASE_COLORS.PALETTE_YELLOW,
    CHART_BASE_COLORS.PALETTE_GREEN,
    CHART_BASE_COLORS.PALETTE_PURPLE,
    CHART_BASE_COLORS.PALETTE_ORANGE,
    CHART_BASE_COLORS.PALETTE_BROWN,
  ],
  // 円グラフ・ドーナツグラフのラベルスタイル
  pieLabels: {
    // テキスト色
    color: '#000000',
    // フォント設定
    font: {
      weight: 'bold',
      size: 14,
    },
    // 背景色（半透明の白）
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    // 縁取り色
    borderColor: 'white',
    // 縁取りの太さ
    borderWidth: 1.5,
    // 角丸
    borderRadius: 3,
    // パディング
    padding: {
      top: 4,
      bottom: 4,
      left: 6,
      right: 6,
    },
  },
};

// パレットから色を取得（インデックス指定、透明度指定可能）
export const getPaletteColor = (index, alpha = 1) => {
  const palette = CHART_COLORS.palette;
  const color = palette[index % palette.length];
  return rgbaString(color, alpha);
};

/**
 * CSS変数を生成する
 * :root に設定するCSS変数のオブジェクトを返す
 */
export const generateCSSVariables = () => ({
  // 基本色
  '--black': CHART_BASE_COLORS.BLACK.hex,

  // オレンジ系
  '--orange-main': CHART_BASE_COLORS.ORANGE_MAIN.hex,
  '--orange-sub': CHART_BASE_COLORS.ORANGE_SUB.hex,
  '--orange-sub-light': CHART_BASE_COLORS.ORANGE_SUB_LIGHT.hex,
  '--orange-main-rgb': `${CHART_BASE_COLORS.ORANGE_MAIN.r}, ${CHART_BASE_COLORS.ORANGE_MAIN.g}, ${CHART_BASE_COLORS.ORANGE_MAIN.b}`,

  // ブルー系
  '--blue-main': CHART_BASE_COLORS.BLUE_MAIN.hex,
  '--blue-sub': CHART_BASE_COLORS.BLUE_SUB.hex,
  '--blue-sub-light': CHART_BASE_COLORS.BLUE_SUB_LIGHT.hex,
  '--blue-main-rgb': `${CHART_BASE_COLORS.BLUE_MAIN.r}, ${CHART_BASE_COLORS.BLUE_MAIN.g}, ${CHART_BASE_COLORS.BLUE_MAIN.b}`,

  // グリーン系
  '--green-main': CHART_BASE_COLORS.GREEN_MAIN.hex,
  '--green-sub': CHART_BASE_COLORS.GREEN_SUB.hex,
  '--green-sub-light': CHART_BASE_COLORS.GREEN_SUB_LIGHT.hex,
  '--green-main-rgb': `${CHART_BASE_COLORS.GREEN_MAIN.r}, ${CHART_BASE_COLORS.GREEN_MAIN.g}, ${CHART_BASE_COLORS.GREEN_MAIN.b}`,

  // グレー系
  '--gray-main': CHART_BASE_COLORS.GRAY_MAIN.hex,
  '--gray-sub': CHART_BASE_COLORS.GRAY_SUB.hex,
  '--gray-side': CHART_BASE_COLORS.GRAY_SIDE.hex,

  // その他
  '--pink': CHART_BASE_COLORS.PINK.hex,

  // エイリアス
  '--primary-color': CHART_BASE_COLORS.ORANGE_MAIN.hex,
  '--primary-sub-color': CHART_BASE_COLORS.ORANGE_SUB.hex,
  '--secondary-color': CHART_BASE_COLORS.BLUE_MAIN.hex,
  '--secondary-sub-color': CHART_BASE_COLORS.BLUE_SUB.hex,

  // 学校/企業管理者サイト(/teacher)のヘッダー帯色（スティールブルー）・#3185
  '--school-admin-header-color': CHART_BASE_COLORS.STEEL_BLUE.hex,
  // ヘッダー帯の実効色。既定はオレンジ（利用者/事務局）。main.js が /teacher 配下では
  // mount 前に --school-admin-header-color へ同期上書きし、オレンジ→青のちらつきを防ぐ・#3185
  '--header-band-color': 'var(--primary-color)',
});

/**
 * CSS変数をDOMに適用する
 * アプリケーション起動時に呼び出すことで、color.jsの色定義をCSS変数として利用可能にする
 */
export const applyCSSVariables = () => {
  const variables = generateCSSVariables();
  const root = document.documentElement;

  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};

export default {
  CHART_BASE_COLORS,
  CHART_COLORS,
  rgbString,
  rgbaString,
  getPaletteColor,
  generateCSSVariables,
  applyCSSVariables,
};
